import { $fetch, FetchError, type $Fetch, type FetchOptions } from 'ofetch'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * Глобальный access-токен. Хранится в памяти (НЕ localStorage),
 * чтобы XSS не мог его украсть. После F5 — restoreSession() через /me
 * восстановит сессию по refresh-cookie.
 */
let accessToken: string | null = null
let refreshing: Promise<string | null> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/**
 * 401 → один параллельный refresh, очередь всех request'ов ждёт его.
 * Если refresh упал — clear token + redirect на /login через onUnauthenticated callback.
 */
let onUnauthenticatedCb: (() => void) | null = null
export function onUnauthenticated(cb: () => void) {
  onUnauthenticatedCb = cb
}

async function tryRefresh(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await $fetch<{ accessToken: string }>(`${API_BASE}/admin/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        })
        accessToken = res.accessToken
        return accessToken
      } catch {
        accessToken = null
        if (onUnauthenticatedCb) onUnauthenticatedCb()
        return null
      } finally {
        refreshing = null
      }
    })()
  }
  return refreshing
}

export const api: $Fetch = $fetch.create({
  baseURL: API_BASE,
  credentials: 'include',
  retry: 0,
  onRequest({ options }) {
    const headers = new Headers(options.headers as HeadersInit | undefined)
    if (accessToken && !headers.has('authorization')) {
      headers.set('authorization', `Bearer ${accessToken}`)
    }
    options.headers = headers
  },
})

export interface RequestOptions {
  method?: string
  body?: unknown
  headers?: HeadersInit
  /** Не пытаться refresh при 401 (для login/refresh самого) */
  noRefresh?: boolean
}

/**
 * Универсальный helper с 401-retry. Используем его вместо api(...) напрямую
 * везде, где нужен auth.
 *
 *   const data = await request<DashboardStats>('/admin/stats/dashboard')
 */
export async function request<T = unknown>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const { noRefresh, ...rest } = options
  const fetchOpts = rest as FetchOptions<'json'>
  try {
    return await api<T>(url, fetchOpts)
  } catch (err) {
    if (!(err instanceof FetchError) || err.response?.status !== 401 || noRefresh) {
      throw err
    }
    const refreshed = await tryRefresh()
    if (!refreshed) throw err
    return await api<T>(url, fetchOpts)
  }
}
